export enum CustomerSatisfactionStatus {
  Satisfied = 1,
  Unsatisfied = 2,
  NoResponse = 3,
}

export type CustomerSatisfactionStatusKey =
  | "satisfied"
  | "unsatisfied"
  | "no_response"
  | "undefined";

export enum CustomerDissatisfactionOperationalSignal {
  ProductAvailability = "productAvailability",
  PriceOrOrderChange = "priceOrOrderChange",
  PreparationTiming = "preparationTiming",
  DeliveryTiming = "deliveryTiming",
}

export type CustomerDissatisfactionOperationalSignalKey =
  | CustomerDissatisfactionOperationalSignal
  | `${CustomerDissatisfactionOperationalSignal}`;

export const CUSTOMER_DISSATISFACTION_OPERATIONAL_SIGNALS: CustomerDissatisfactionOperationalSignal[] =
  [
    CustomerDissatisfactionOperationalSignal.ProductAvailability,
    CustomerDissatisfactionOperationalSignal.PriceOrOrderChange,
    CustomerDissatisfactionOperationalSignal.PreparationTiming,
    CustomerDissatisfactionOperationalSignal.DeliveryTiming,
  ];

export const getCustomerDissatisfactionOperationalSignalLabel = (
  signal?: string | null,
) => {
  switch (signal) {
    case CustomerDissatisfactionOperationalSignal.ProductAvailability:
      return "operational_signal_product_availability";
    case CustomerDissatisfactionOperationalSignal.PriceOrOrderChange:
      return "operational_signal_price_or_order_change";
    case CustomerDissatisfactionOperationalSignal.PreparationTiming:
      return "operational_signal_preparation_timing";
    case CustomerDissatisfactionOperationalSignal.DeliveryTiming:
      return "operational_signal_delivery_timing";
    default:
      return signal ?? "not_determined";
  }
};

export const getCustomerDissatisfactionOperationalSignalColor = (
  signal?: string | null,
) => {
  switch (signal) {
    case CustomerDissatisfactionOperationalSignal.ProductAvailability:
      return "volcano";
    case CustomerDissatisfactionOperationalSignal.PriceOrOrderChange:
      return "gold";
    case CustomerDissatisfactionOperationalSignal.PreparationTiming:
      return "geekblue";
    case CustomerDissatisfactionOperationalSignal.DeliveryTiming:
      return "purple";
    default:
      return "default";
  }
};

export const getCustomerSatisfactionStatusLabel = (
  status?: CustomerSatisfactionStatus | number | null,
) => {
  switch (status) {
    case CustomerSatisfactionStatus.Satisfied:
      return "satisfied";
    case CustomerSatisfactionStatus.Unsatisfied:
      return "unsatisfied";
    case CustomerSatisfactionStatus.NoResponse:
      return "no_response";
    default:
      return "not_determined";
  }
};

export const getCustomerSatisfactionStatusColor = (
  status?: CustomerSatisfactionStatus | number | null,
) => {
  switch (status) {
    case CustomerSatisfactionStatus.Satisfied:
      return "green";
    case CustomerSatisfactionStatus.Unsatisfied:
      return "red";
    case CustomerSatisfactionStatus.NoResponse:
      return "orange";
    default:
      return "default";
  }
};

export const getCustomerSatisfactionSummaryKey = (
  summary?: { key?: string | null; value?: number | null } | null,
  fallbackStatus?: CustomerSatisfactionStatus | number | null,
): CustomerSatisfactionStatusKey => {
  if (summary?.key === "satisfied" || summary?.value === 1) return "satisfied";
  if (summary?.key === "unsatisfied" || summary?.value === 2) {
    return "unsatisfied";
  }
  if (summary?.key === "no_response" || summary?.value === 3) {
    return "no_response";
  }

  if (fallbackStatus === CustomerSatisfactionStatus.Satisfied) {
    return "satisfied";
  }
  if (fallbackStatus === CustomerSatisfactionStatus.Unsatisfied) {
    return "unsatisfied";
  }
  if (fallbackStatus === CustomerSatisfactionStatus.NoResponse) {
    return "no_response";
  }

  return "undefined";
};

export const getCustomerSatisfactionSummaryLabel = (
  summary?: {
    label?: string | null;
    key?: string | null;
    value?: number | null;
  } | null,
  fallbackStatus?: CustomerSatisfactionStatus | number | null,
) => {
  if (summary?.label) return summary.label;

  const key = getCustomerSatisfactionSummaryKey(summary, fallbackStatus);
  return key === "undefined" ? "not_determined" : key;
};

export const getCustomerSatisfactionSummaryColor = (
  summary?: { key?: string | null; value?: number | null } | null,
  fallbackStatus?: CustomerSatisfactionStatus | number | null,
) => {
  switch (getCustomerSatisfactionSummaryKey(summary, fallbackStatus)) {
    case "satisfied":
      return "green";
    case "unsatisfied":
      return "red";
    case "no_response":
      return "orange";
    default:
      return "default";
  }
};
